// external
import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Chip, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import DOMPurify from 'dompurify';

// internal
import useService from '../../hooks/service.hook';
import ThreadsService from '../../services/threads.service';
import { User } from '../../models/user.models';
import { formatThreadAudienceLabels, formatThreadDate } from '../../helpers/general.helpers';
import { UserAvatar } from '../user-avatar/user-avatar.component';
import COESkeletonComponent from '../general/coe-skeleton/coe-skeleton.component';
import GuardianService from '../../services/guardian.service';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import { palette } from '../../theme';
import { ThreadAudience } from '../../models/thread.model';

import './coe-view-thread.component.css';

export interface TeamGroupOption {
  id: string | number;
  name: string;
}

export interface COEViewThreadProps {
  threadId: number | string;
  onDeleteThread: any;
  refreshThreads: any;
  softRefreshThreads?: () => void;
  isCoach: boolean;
  isGuardian?: boolean;
  teamName?: string;
  groups?: TeamGroupOption[];
}

interface ThreadDetail {
  title: string;
  message: string;
  created_at: string;
  created_by: string;
  creator_profile_image?: string | null;
  id: string | number;
  updated_at?: string;
  audience?: ThreadAudience | null;
}

interface Reply {
  id: number;
  message: string;
  created_by: string;
  created_at: string;
  deleted: boolean;
  deleted_at?: string;
  deleted_by?: string;
  creator_profile_image?: string | null;
}

const getAudiencePillStyle = (label: string): React.CSSProperties => {
  if (label === 'Guardians') {
    return {
      backgroundColor: palette.warning.light,
      color: palette.warning.dark,
    };
  }
  return {
    backgroundColor: palette.primary.light,
    color: palette.primary.main,
  };
};

export const MessageEmptyState: FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      color: palette.text.disabled,
      paddingTop: 100,
      boxSizing: 'border-box',
    }}
  >
    <MailOutlineIcon sx={{ fontSize: 48, color: palette.grey[300], mb: 1.5 }} />
    <Typography style={{ fontSize: 14, color: palette.text.disabled }}>Select a message to read</Typography>
  </div>
);

const COEViewThread: FC<COEViewThreadProps> = (props) => {
  const threadsService = useService(ThreadsService);
  const guardianService = useService(GuardianService);
  const [loading, setLoading] = useState<boolean>(true);
  const { id } = useParams();
  const { showSnackbar } = useSnackbarHelper();
  const [thread, setThread] = useState<ThreadDetail>();
  const [replies, setReplies] = useState<Reply[]>([]);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    setIsEdit(false);
    setReplyText('');
    if (props.isGuardian) {
      getSelectedThreadForGuardian(props.threadId);
    } else {
      getSelectedThread(props.threadId);
    }
  }, [props.threadId]);

  const applyThreadResponse = (data: any) => {
    setThread({
      id: data.id,
      title: data.title,
      message: data.message,
      created_at: data.created_at,
      created_by: data.created_by,
      creator_profile_image: data.creator_profile_image ?? null,
      updated_at: data.updated_at,
      audience: data.audience ?? null,
    });
    setReplies(data.replies || []);
    setMessage(data.message);
  };

  const getSelectedThread = (threadId: any, options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    threadsService
      .getThread(id, threadId)
      .then((response) => {
        setLoading(false);
        applyThreadResponse(response.data.data);
        props.softRefreshThreads?.();
      })
      .catch(() => {
        setLoading(false);
        showSnackbar('There was an error in viewing this thread. Please try again later', 'error');
      });
  };

  const getSelectedThreadForGuardian = (threadId: any, options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    guardianService
      .getWardTeamThread(id as any, threadId)
      .then((response) => {
        setLoading(false);
        applyThreadResponse(response.data.data);
        props.softRefreshThreads?.();
      })
      .catch(() => {
        setLoading(false);
        showSnackbar('There was an error in viewing this thread. Please try again later', 'error');
      });
  };

  const reloadThread = (silent = true) => {
    if (props.isGuardian) {
      getSelectedThreadForGuardian(props.threadId, { silent });
    } else {
      getSelectedThread(props.threadId, { silent });
    }
  };

  const handleEditMessage = () => {
    setLoading(true);
    threadsService
      .updateThread({ title: thread?.title, message: message } as any, id, props.threadId)
      .then(() => {
        setIsEdit(false);
        setLoading(false);
        props.refreshThreads();
      })
      .catch(() => {
        setIsEdit(false);
        setLoading(false);
        showSnackbar('There was an error while updating this message. Please try again later.', 'error');
      });
  };

  const handleDeleteReply = (replyId: string | number) => {
    threadsService
      .deleteReply(thread?.id, replyId)
      .then(() => {
        reloadThread(true);
      })
      .catch(() => {
        showSnackbar('There was an error while deleting this reply. Please try again later.', 'error');
      });
  };

  const handleSendReply = () => {
    if (!replyText.trim()) {
      showSnackbar('Please write a reply before sending', 'error');
      return;
    }
    setSendingReply(true);
    threadsService
      .postReply(replyText.trim(), props.threadId)
      .then(() => {
        setReplyText('');
        setSendingReply(false);
        reloadThread(true);
        showSnackbar('Reply sent successfully', 'success');
      })
      .catch(() => {
        setSendingReply(false);
        showSnackbar('There was an error sending your reply. Please try again later', 'error');
      });
  };

  const audienceLabels = formatThreadAudienceLabels(thread?.audience, props.groups);
  const user: User = JSON.parse(window.localStorage.getItem('user') as string);
  const userName = `${user.firstName} ${user.lastName}`;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {loading ? (
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <COESkeletonComponent />
          <COESkeletonComponent />
          <COESkeletonComponent />
        </div>
      ) : (
        <>
          {/* Header */}
          <div style={{ padding: '32px 40px 0 40px', flexShrink: 0 }}>
            <div
              style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}
            >
              <Typography
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: palette.text.primary,
                  lineHeight: 1.3,
                  flex: 1,
                  paddingRight: 12,
                }}
              >
                {thread?.title}
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                {isEdit && (
                  <Chip
                    label="Editing"
                    size="small"
                    sx={{
                      backgroundColor: palette.grey[200],
                      color: palette.text.secondary,
                      fontWeight: 600,
                      fontSize: 11,
                      mr: 1,
                    }}
                  />
                )}
                <Tooltip title="Refresh">
                  <IconButton
                    size="small"
                    onClick={() => reloadThread(true)}
                    disabled={loading || sendingReply}
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                    }}
                    aria-label="Refresh thread"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {props.isCoach && (
                  <>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => setIsEdit(true)}
                        sx={{ color: palette.text.secondary, '&:hover': { color: palette.primary.main } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => props.onDeleteThread(props.threadId)}
                        sx={{ color: palette.text.secondary, '&:hover': { color: palette.error.main } }}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>

            {/* Sender + audience pills */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <UserAvatar
                src={thread?.creator_profile_image}
                name={thread?.created_by ?? ''}
                size={40}
                fontSize="0.85rem"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Typography style={{ fontSize: 14, fontWeight: 600, color: palette.text.primary }}>
                    {thread?.created_by}
                  </Typography>
                  {audienceLabels.map((label) => (
                    <span
                      key={label}
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '3px 10px',
                        borderRadius: 12,
                        whiteSpace: 'nowrap',
                        ...getAudiencePillStyle(label),
                      }}
                    >
                      {label}
                    </span>
                  ))}
                </div>
                <Typography style={{ fontSize: 12, color: palette.text.disabled, marginTop: 2 }}>
                  {formatThreadDate(thread?.created_at as string)}
                </Typography>
              </div>
            </div>

            <div style={{ height: 1, backgroundColor: palette.grey[100], marginTop: 12 }} />
          </div>

          {/* Message body + replies (scrollable) */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              padding: '16px 40px 8px 40px',
            }}
          >
            {isEdit ? (
              <ReactQuill theme="snow" value={message} onChange={setMessage} style={{ width: '100%' }} />
            ) : (
              <div
                className="thread-message-body ql-editor"
                style={{
                  padding: 0,
                  height: 'auto',
                  minHeight: 0,
                  overflow: 'visible',
                  fontSize: 14,
                  color: palette.text.primary,
                  lineHeight: 1.7,
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(message || '', {
                    ADD_ATTR: ['target'],
                  }),
                }}
              />
            )}

            {isEdit && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 10,
                  paddingTop: 16,
                }}
              >
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => {
                    setMessage(thread?.message);
                    setIsEdit(false);
                  }}
                >
                  Cancel
                </Button>
                <Button variant="contained" color="primary" size="small" onClick={handleEditMessage}>
                  Update
                </Button>
              </div>
            )}

            {/* Replies list — sits directly under body content */}
            {!isEdit && replies.length > 0 && (
              <div style={{ marginTop: 36 }}>
                <Typography
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: palette.text.secondary,
                    marginBottom: 14,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}
                </Typography>
                {replies.map((reply) => (
                  <div
                    key={reply.id}
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: '12px 0',
                      borderTop: `1px solid ${palette.grey[100]}`,
                    }}
                  >
                    <UserAvatar
                      src={reply.creator_profile_image}
                      name={reply.created_by}
                      size={32}
                      fontSize="0.65rem"
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <Typography style={{ fontSize: 13, fontWeight: 600, color: palette.text.primary }}>
                          {reply.created_by === userName ? 'Me' : reply.created_by}
                        </Typography>
                        <Typography style={{ fontSize: 11, color: palette.text.disabled }}>
                          {formatThreadDate(reply.created_at)}
                        </Typography>
                        {(props.isCoach || reply.created_by === userName) && !reply.deleted && (
                          <IconButton
                            onClick={() => handleDeleteReply(reply.id)}
                            size="small"
                            sx={{ color: palette.error.main, ml: 'auto' }}
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                      <Typography
                        style={{
                          fontSize: 13,
                          color: reply.deleted ? palette.text.disabled : palette.text.primary,
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {reply.deleted ? '[Message was deleted]' : reply.message}
                      </Typography>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pinned reply composer — always visible for non-guardians */}
          {!isEdit && !props.isGuardian && (
            <div
              style={{
                flexShrink: 0,
                padding: '12px 40px 16px 40px',
                borderTop: `1px solid ${palette.grey[100]}`,
                backgroundColor: palette.background.default,
              }}
            >
              <div
                style={{
                  border: `1px solid ${palette.divider}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={6}
                  placeholder="Write a reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      padding: '12px 16px',
                      fontSize: 14,
                      color: palette.text.primary,
                      lineHeight: 1.6,
                    },
                  }}
                />
                <div
                  style={{
                    padding: '8px 16px',
                    backgroundColor: palette.grey[50],
                    borderTop: `1px solid ${palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    onClick={handleSendReply}
                    color="primary"
                    variant="contained"
                    disabled={sendingReply}
                    sx={{ borderRadius: '8px', px: 2.5, fontWeight: 600, fontSize: 13 }}
                  >
                    {sendingReply ? 'Sending…' : 'Send'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default COEViewThread;
